import { useState, useEffect, useRef } from 'react';

export default function ModularStructure() {
  const [activeStage, setActiveStage] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  const stages = [
    {
      number: '01',
      title: 'Factory Production',
      description: 'Prefabricated in a controlled manufacturing environment, with assembly processes designed to reduce on-site disruption and waste.',
      image: '/images/stage-01-factory-production.webp',
    },
    {
      number: '02',
      title: 'Transport',
      description: 'Compact design allows standard shipping to any location worldwide.',
      image: '/images/ai-flatbed-truck-transporting-modular-prefa-67c354.webp',
    },
    {
      number: '03',
      title: 'Foundation Prep',
      description: 'Site preparation requirements depend on your location, ground conditions and chosen model. A site assessment helps determine the scope.',
      image: '/images/ai-construction-site-foundation-preparation-4c6683.webp',
    },
    {
      number: '04',
      title: 'Assembly',
      description: 'On-site assembly timelines vary by model, site readiness and local contractor availability. Timelines are confirmed during project planning.',
      image: '/images/ai-construction-workers-installing-assembli-6eec2b.webp',
    },
    {
      number: '05',
      title: 'Activation',
      description: 'Smart systems initialization and final quality checks ensure perfect operation.',
      image: '/images/ai-technician-activating-smart-home-systems-d406bb.webp',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % stages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [stages.length]);

  return (
    <section className="relative py-32 bg-gradient-to-b from-[#000000] via-[#0a0e1a] to-[#000000]">
      <div className="max-w-7xl mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Left Side - Sticky */}
          <div className="lg:col-span-2 lg:sticky lg:top-32 h-fit">
            <h2 className="text-6xl font-thin leading-tight mb-6">
              Built for Speed,<br />Designed for Decades
            </h2>
            <p className="text-gray-400 text-lg mb-8 leading-relaxed">
              Our prefabricated modular construction delivers unmatched efficiency without compromising quality or longevity.
            </p>
            
            {/* Technical Specs */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-cyan-400 rounded-full shadow-[0_0_10px_rgba(0,217,255,0.8)]"></div>
                <span className="text-gray-300">Prefabricated off-site construction</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-cyan-400 rounded-full shadow-[0_0_10px_rgba(0,217,255,0.8)]"></div>
                <span className="text-gray-300">Aluminium and stainless-steel material systems</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-cyan-400 rounded-full shadow-[0_0_10px_rgba(0,217,255,0.8)]"></div>
                <span className="text-gray-300">Panoramic glazing with considered layouts</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-cyan-400 rounded-full shadow-[0_0_10px_rgba(0,217,255,0.8)]"></div>
                <span className="text-gray-300">Fully relocatable design</span>
              </div>
            </div>
          </div>

          {/* Right Side - Timeline */}
          <div className="lg:col-span-3 space-y-12" ref={sectionRef}>
            {stages.map((stage, index) => (
              <div
                key={index}
                className={`relative transition-all duration-500 ${
                  activeStage === index ? 'opacity-100' : 'opacity-40'
                }`}
              >
                <div className="bg-gradient-to-br from-[#14141e]/60 to-[#0a0a0f]/60 backdrop-blur-xl border border-cyan-500/30 rounded-lg p-8 hover:border-cyan-400/60 transition-all">
                  {/* Stage Number */}
                  <div className="absolute -top-6 -left-6 text-8xl font-thin text-transparent" style={{
                    WebkitTextStroke: '1px rgba(0,217,255,0.5)',
                  }}>
                    {stage.number}
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <h3 className="text-3xl font-medium mb-4 text-white">{stage.title}</h3>
                    <div className="w-full h-64 rounded-lg overflow-hidden mb-4">
                      <img 
                        src={stage.image} 
                        alt={stage.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-gray-400 text-sm leading-relaxed">{stage.description}</p>
                  </div>
                </div>

                {/* Connecting Line */}
                {index < stages.length - 1 && (
                  <div className="absolute left-1/2 -bottom-6 w-0.5 h-12 bg-gradient-to-b from-cyan-500/50 to-transparent">
                    {activeStage === index && (
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 bg-cyan-400 rounded-full shadow-[0_0_15px_rgba(0,217,255,0.8)] animate-pulse"></div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
